<script setup lang="ts" generic="T">
  import { useVirtualizer } from "@tanstack/vue-virtual";
  import { computed, ref } from "vue";

  type Props = {
    items: T[];
    itemSize: number;
    overscan?: number;
  };

  const props = withDefaults(defineProps<Props>(), {
    overscan: 5,
  });

  defineSlots<{
    default(props: { item: T; index: number }): unknown;
  }>();

  const viewportRef = ref<HTMLElement | null>(null);

  const virtualizer = useVirtualizer(
    computed(() => ({
      count: props.items.length,
      getScrollElement: () => viewportRef.value,
      estimateSize: () => props.itemSize,
      overscan: props.overscan,
    })),
  );

  function scrollToOffset(offset: number, options?: { behavior?: "auto" | "smooth" }) {
    virtualizer.value.scrollToOffset(offset, options);
  }

  function scrollToIndex(index: number, options?: { behavior?: "auto" | "smooth" }) {
    virtualizer.value.scrollToIndex(index, options);
  }

  defineExpose({ scrollToOffset, scrollToIndex });
</script>

<template>
  <div ref="viewportRef" :class="$style.viewport">
    <div :class="$style.spacer" :style="{ height: `${virtualizer.getTotalSize()}px` }">
      <div
        v-for="virtualRow in virtualizer.getVirtualItems()"
        :key="String(virtualRow.key)"
        :data-index="virtualRow.index"
        :class="$style.row"
        :style="{ transform: `translateY(${virtualRow.start}px)` }"
      >
        <slot :item="items[virtualRow.index]" :index="virtualRow.index" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  .viewport {
    width: 100%;
    height: 100%;

    overflow-y: auto;
  }

  .spacer {
    position: relative;
    width: 100%;
  }

  .row {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }
</style>
